import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Calendar, TrendingUp, Award, Target, Plus, Apple, Coffee, Utensils, Heart, Zap, Clock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import SoftCard from '../components/UI/SoftCard';
import SoftButton from '../components/UI/SoftButton';

const EnhancedDashboard: React.FC = () => {
  const { user } = useAuth();
  const [todaysMeals] = useState([
    { 
      id: 1, 
      type: 'Breakfast', 
      name: 'Avocado Toast with Eggs',
      calories: 420,
      time: '8:30 AM',
      icon: <Coffee className="w-5 h-5" />,
      color: 'from-orange-400 to-red-400',
      image: 'https://images.pexels.com/photos/1351238/pexels-photo-1351238.jpeg?auto=compress&cs=tinysrgb&w=200'
    },
    { 
      id: 2, 
      type: 'Lunch', 
      name: 'Mediterranean Quinoa Bowl',
      calories: 650,
      time: '1:15 PM',
      icon: <Utensils className="w-5 h-5" />,
      color: 'from-emerald-400 to-teal-400',
      image: 'https://images.pexels.com/photos/1640777/pexels-photo-1640777.jpeg?auto=compress&cs=tinysrgb&w=200'
    },
    { 
      id: 3, 
      type: 'Snack', 
      name: 'Greek Yogurt with Berries',
      calories: 180,
      time: '3:45 PM',
      icon: <Apple className="w-5 h-5" />,
      color: 'from-purple-400 to-pink-400',
      image: 'https://images.pexels.com/photos/1092730/pexels-photo-1092730.jpeg?auto=compress&cs=tinysrgb&w=200'
    }
  ]);

  const healthMetrics = [
    { label: 'Calories Today', value: '1,250', goal: '2,000', percentage: 62, icon: <Zap className="w-6 h-6" />, color: 'from-orange-400 to-red-400' },
    { label: 'Protein', value: '45g', goal: '80g', percentage: 56, icon: <Target className="w-6 h-6" />, color: 'from-blue-400 to-cyan-400' },
    { label: 'Water', value: '6 cups', goal: '8 cups', percentage: 75, icon: <Heart className="w-6 h-6" />, color: 'from-cyan-400 to-blue-400' },
    { label: 'Steps', value: '8,542', goal: '10,000', percentage: 85, icon: <TrendingUp className="w-6 h-6" />, color: 'from-emerald-400 to-teal-400' }
  ];

  const quickActions = [
    { title: 'Log Food', description: 'Add a meal or snack', icon: <Plus className="w-6 h-6" />, color: 'from-emerald-400 to-teal-500' },
    { title: 'View Progress', description: 'Check your nutrition trends', icon: <TrendingUp className="w-6 h-6" />, color: 'from-blue-400 to-cyan-500' },
    { title: 'Plan Meals', description: 'Create weekly meal plans', icon: <Calendar className="w-6 h-6" />, color: 'from-purple-400 to-pink-500' },
    { title: 'Scan Food', description: 'Use AI food recognition', icon: <Award className="w-6 h-6" />, color: 'from-amber-400 to-yellow-500' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-emerald-50 to-teal-50 px-4 sm:px-6 lg:px-8 py-8">
      <div className="max-w-7xl mx-auto">
        {/* Welcome Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-gray-800 mb-2">
                Welcome back, {user?.name || 'Friend'}! 👋
              </h1>
              <p className="text-lg text-gray-600">
                Here's your nutrition overview for today. You're doing great!
              </p>
            </div>
            <div className="hidden md:block">
              <SoftCard className="p-4">
                <div className="flex items-center space-x-3">
                  <Clock className="w-6 h-6 text-emerald-500" />
                  <div>
                    <p className="text-sm text-gray-500">Today</p>
                    <p className="font-semibold text-gray-800">{new Date().toLocaleDateString()}</p>
                  </div>
                </div>
              </SoftCard>
            </div>
          </div>
        </motion.div>

        {/* Health Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {healthMetrics.map((metric, index) => (
            <motion.div
              key={metric.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
            >
              <SoftCard className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${metric.color} flex items-center justify-center text-white`}>
                    {metric.icon}
                  </div>
                  <span className="text-2xl font-bold text-gray-800">{metric.percentage}%</span>
                </div>
                <h3 className="text-sm font-medium text-gray-600 mb-2">{metric.label}</h3>
                <div className="flex items-baseline space-x-2 mb-3">
                  <span className="text-xl font-bold text-gray-900">{metric.value}</span>
                  <span className="text-sm text-gray-500">of {metric.goal}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <motion.div
                    className={`h-2 rounded-full bg-gradient-to-r ${metric.color}`}
                    initial={{ width: 0 }}
                    animate={{ width: `${metric.percentage}%` }}
                    transition={{ duration: 1, delay: 0.5 + index * 0.1 }}
                  />
                </div>
              </SoftCard>
            </motion.div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Today's Meals */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <SoftCard className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-800">Today's Meals</h2>
                  <SoftButton size="sm">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Meal
                  </SoftButton>
                </div>

                <div className="space-y-4">
                  {todaysMeals.map((meal) => (
                    <motion.div
                      key={meal.id}
                      whileHover={{ scale: 1.02 }}
                      className="flex items-center space-x-4 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors cursor-pointer"
                    >
                      <img 
                        src={meal.image} 
                        alt={meal.name}
                        className="w-16 h-16 rounded-lg object-cover"
                      />
                      <div className={`w-12 h-12 rounded-full bg-gradient-to-r ${meal.color} flex items-center justify-center text-white`}>
                        {meal.icon}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900">{meal.name}</h3>
                        <p className="text-sm text-gray-600">{meal.type} • {meal.time}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-gray-900">{meal.calories} cal</p>
                        <p className="text-sm text-gray-500">calories</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </SoftCard>
            </motion.div>
          </div>

          {/* Quick Actions */}
          <div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <SoftCard className="p-6">
                <h2 className="text-xl font-bold text-gray-800 mb-6">Quick Actions</h2>
                <div className="space-y-3">
                  {quickActions.map((action) => (
                    <motion.div
                      key={action.title}
                      whileHover={{ scale: 1.02 }}
                      className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                    >
                      <div className={`w-10 h-10 rounded-lg bg-gradient-to-r ${action.color} flex items-center justify-center text-white`}>
                        {action.icon}
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900">{action.title}</h3>
                        <p className="text-sm text-gray-600">{action.description}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </SoftCard>
            </motion.div>

            {/* Weekly Progress */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="mt-6"
            >
              <SoftCard className="p-6">
                <h3 className="text-lg font-bold text-gray-800 mb-4">This Week's Progress</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Meals logged</span>
                    <span className="font-semibold text-gray-800">18/21</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="w-5/6 h-2 bg-gradient-to-r from-emerald-400 to-teal-500 rounded-full"></div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Nutrition goals met</span>
                    <span className="font-semibold text-gray-800">5/7 days</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="w-4/5 h-2 bg-gradient-to-r from-blue-400 to-cyan-500 rounded-full"></div>
                  </div>
                </div>
              </SoftCard>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnhancedDashboard;